import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { validatePassword, PASSWORD_HELPER_TEXT } from '../../utils/passwordValidation';
import { isStalePhoneToken } from '../../utils/phoneAuthErrors';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { OtpVerificationModal } from './OtpVerificationModal';
import { PhoneOtpModal } from './PhoneOtpModal';
import { SocialAuthButtons } from './SocialAuthButtons';
import { AuthBackground } from './auth/AuthBackground';
import { BrandHero } from './auth/BrandHero';
import { ChargingProgressBar } from './auth/ChargingProgressBar';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/authService';
import { otpService } from '../../services/otpService';
import { captureError } from '../../lib/errorHandler';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';

type WizardStep = 1 | 2;

export function RegisterScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  // Arriving from the OTP login modal for a number that has no account: it is already
  // SMS-verified, so seed the form with it and reuse that Firebase token rather than
  // burning a second SMS on the same number.
  const prefill = (location.state ?? {}) as { phone?: string; phoneIdToken?: string };
  // Fixed at mount, deliberately not derived from the phoneIdToken state: clearing that
  // (an edited number, an expired token) must not restructure the wizard mid-flow.
  const [isOtpSignup] = useState(() => !!prefill.phoneIdToken);

  // Wizard state
  const [step, setStep] = useState<WizardStep>(1);
  const [direction, setDirection] = useState(1);

  // Form fields (kept at top level for OTP flow compatibility)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState(prefill.phone ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [showPhoneOtpModal, setShowPhoneOtpModal] = useState(false);
  const [phoneIdToken, setPhoneIdToken] = useState<string | null>(prefill.phoneIdToken ?? null);
  const [showOptionalPassword, setShowOptionalPassword] = useState(false);

  const { register, isLoading, error, clearError } = useAuthStore();

  // --- Step validation ---
  const validateStep = (s: WizardStep): boolean => {
    switch (s) {
      case 1:
        if (!firstName.trim()) {
          setValidationError('First name is required');
          return false;
        }
        if (firstName.trim().length < 2) {
          setValidationError('First name must be at least 2 characters');
          return false;
        }
        if (!lastName.trim()) {
          setValidationError('Last name is required');
          return false;
        }
        if (!phone.trim()) {
          setValidationError('Phone number is required');
          return false;
        }
        if (!/^[6-9]\d{9}$/.test(phone.replace(/\D/g, ''))) {
          setValidationError('Please enter a valid 10-digit Indian phone number');
          return false;
        }
        if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          setValidationError('Please enter a valid email');
          return false;
        }
        setValidationError(null);
        return true;
      case 2: {
        // OTP sign-ups may leave it blank — SMS is their login method. A partially
        // filled field is still an error, so only skip validation when both are empty.
        if (isOtpSignup && !password && !confirmPassword) {
          setValidationError(null);
          return true;
        }
        const passwordError = validatePassword(password);
        if (passwordError) {
          setValidationError(passwordError);
          return false;
        }
        if (password !== confirmPassword) {
          setValidationError('Passwords do not match');
          return false;
        }
        setValidationError(null);
        return true;
      }
      default:
        return true;
    }
  };

  const goToStep = (nextStep: WizardStep) => {
    setDirection(nextStep > step ? 1 : -1);
    setStep(nextStep);
  };

  const handleNext = () => {
    clearError();
    if (!validateStep(step)) return;
    if (step < 2) {
      goToStep((step + 1) as WizardStep);
    }
  };

  // OTP sign-ups collapse to one screen: the number is already proven and the password
  // is optional, so there is nothing left to gate behind a second step.
  const totalSteps = isOtpSignup ? 1 : 2;
  const isLastStep = step >= totalSteps;

  const handleBack = () => {
    setValidationError(null);
    clearError();
    if (step > 1) {
      goToStep((step - 1) as WizardStep);
    } else {
      navigate('/login');
    }
  };

  const PHONE_REVERIFY_MESSAGE =
    'Phone verification expired. Tap Create Account to verify your number again.';

  // Register + post-success side effects (wallet warning, navigation).
  // Throws on failure so the caller can surface errors however it wants.
  const completeRegistration = async (opts: { phoneToken: string; emailCode?: string }) => {
    const result = await register({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.replace(/\D/g, ''),
      ...(password ? { password } : {}),
      phoneVerificationToken: opts.phoneToken,
      ...(email.trim() && { email: email.trim() }),
      ...(opts.emailCode && { emailOtpCode: opts.emailCode }),
    });
    if (result?.walletCreated === false) {
      toast.error('Account created, but wallet setup failed. Contact support if you have trouble charging.', {
        duration: 10000,
      });
    }
    navigate('/app/map', { replace: true });
    return result;
  };

  const sendEmailOtp = async () => {
    setIsSendingOtp(true);
    try {
      await otpService.sendRegistrationOtp(email.trim());
      setShowOtpModal(true);
      setOtpError(null);
    } catch (err: any) {
      captureError(err, 'Register.sendEmailOtp', { email: email.trim() });
      const msg = err?.response?.data?.message || 'Failed to send OTP';
      setValidationError(msg);
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Once phone is verified, either gate on email OTP or register immediately.
  const proceedAfterPhoneVerified = async (phoneToken: string) => {
    if (email.trim()) {
      await sendEmailOtp();
      return;
    }
    try {
      await completeRegistration({ phoneToken });
    } catch (err: any) {
      captureError(err, 'Register.completeRegistration', { hasEmail: false });
      if (isStalePhoneToken(err)) {
        setPhoneIdToken(null);
        setValidationError(PHONE_REVERIFY_MESSAGE);
        return;
      }
      // User-facing error surfaced by the auth store
    }
  };

  const handleSubmit = async () => {
    clearError();
    // On the collapsed one-screen flow the details were never gated by a Continue tap.
    if (isOtpSignup && !validateStep(1)) return;
    if (!validateStep(2)) return;

    if (!phoneIdToken) {
      try {
        await authService.checkPhone(phone.replace(/\D/g, ''));
      } catch (err: any) {
        captureError(err, 'Register.checkPhone', { phone: phone.replace(/\D/g, '') });
        const status = err?.response?.status;
        const serverMsg = err?.response?.data?.message;
        let msg: string;
        if (status === 409) {
          const toastMsg = serverMsg || 'An account with this phone number already exists';
          toast.error(toastMsg, {
            action: { label: 'Log in', onClick: () => navigate('/login') },
            duration: 8000,
          });
          setValidationError(toastMsg);
          return;
        } else if (status === 429) {
          msg = 'Too many attempts. Please try again in a minute.';
        } else {
          msg = "Couldn't verify this phone number. Please check your connection and try again.";
        }
        setValidationError(msg);
        return;
      }

      // Pre-flight email so we don't burn a phone OTP only to discover the email is taken.
      if (email.trim()) {
        try {
          await authService.checkEmail(email.trim());
        } catch (err: any) {
          captureError(err, 'Register.checkEmail', { email: email.trim() });
          const status = err?.response?.status;
          const serverMsg = err?.response?.data?.message;
          let msg: string;
          if (status === 409) {
            msg = serverMsg || 'An account with this email already exists';
            toast.error(msg, {
              action: { label: 'Log in', onClick: () => navigate('/login') },
              duration: 8000,
            });
          } else if (status === 429) {
            msg = 'Too many attempts. Please try again in a minute.';
          } else {
            msg = "Couldn't verify this email. Please check your connection and try again.";
          }
          setValidationError(msg);
          return;
        }
      }

      setShowPhoneOtpModal(true);
      return;
    }

    await proceedAfterPhoneVerified(phoneIdToken);
  };

  const handlePhoneVerified = async (idToken: string) => {
    setPhoneIdToken(idToken);
    setShowPhoneOtpModal(false);
    await proceedAfterPhoneVerified(idToken);
  };

  const handleOtpVerified = async (code: string) => {
    setIsVerifyingOtp(true);
    setOtpError(null);

    try {
      // phoneIdToken was captured during phone verification (PhoneOtpModal signs out
      // Firebase immediately after, so getIdToken() would fail). The JWT is valid
      // for 1 hour — no refresh needed.
      if (!phoneIdToken) {
        setOtpError('Phone verification session expired. Please restart registration.');
        return;
      }
      await completeRegistration({ phoneToken: phoneIdToken, emailCode: code });
      setShowOtpModal(false);
    } catch (err: any) {
      captureError(err, 'Register.handleOtpVerified', { email: email.trim() });
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message as string | undefined;
      // 409 with an "already exists" message means the account is registered — bounce
      // the user to login instead of letting them retry the OTP forever.
      if (status === 409 && serverMsg && /already exists/i.test(serverMsg)) {
        setShowOtpModal(false);
        setValidationError(serverMsg);
        toast.error(serverMsg, {
          action: { label: 'Log in', onClick: () => navigate('/login') },
          duration: 8000,
        });
        return;
      }
      // Stale phone token: close the email modal so the retry can re-run phone OTP —
      // handleOtpVerified alone can never recover it.
      if (isStalePhoneToken(err)) {
        setPhoneIdToken(null);
        setShowOtpModal(false);
        setValidationError(PHONE_REVERIFY_MESSAGE);
        return;
      }
      setOtpError(serverMsg || 'Registration failed. Please try again.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      await otpService.sendRegistrationOtp(email.trim());
    } catch (err: any) {
      captureError(err, 'Register.handleResendOtp', { email: email.trim() });
      const msg = err?.response?.data?.message || 'Failed to resend OTP';
      setOtpError(msg);
    }
  };

  const clearFieldError = () => {
    setValidationError(null);
    clearError();
  };

  const displayError = validationError || error;

  const slideVariants = {
    enter: (dir: number) => ({ x: dir * 300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir * -300, opacity: 0 }),
  };

  const slideTransition = { type: 'spring' as const, damping: 25, stiffness: 200 };


  return (
    <div className="min-h-screen relative flex flex-col overflow-hidden">
      <AuthBackground />

      {/* Header */}
      <div className="relative z-10 flex items-center px-4 pt-[env(safe-area-inset-top)] py-3">
        <button
          onClick={handleBack}
          className="p-2 -ml-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <span className="ml-2 text-lg font-medium text-foreground">Create Account</span>
      </div>

      {/* Brand + Progress */}
      <div className="relative z-10 px-6 pb-4 flex flex-col items-center">
        <BrandHero compact />
        <div className="w-full max-w-xs mt-5">
          <ChargingProgressBar
            currentStep={step}
            totalSteps={totalSteps}
            labels={isOtpSignup ? ['Your details'] : ['Your details', 'Security']}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="relative z-10 flex-1 px-6 overflow-hidden">
        <AnimatePresence mode="wait" custom={direction}>
          {/* Step 1: Name + Phone */}
          {step === 1 && (
            <motion.div
              key="step1"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={slideTransition}
              className="w-full max-w-sm mx-auto"
            >

              <div className="space-y-4">
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => { setFirstName(e.target.value); clearFieldError(); }}
                      autoComplete="given-name"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => { setLastName(e.target.value); clearFieldError(); }}
                      autoComplete="family-name"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Phone Field */}
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                      +91
                    </span>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="9876543210"
                      value={phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPhone(value);
                        // The token is bound to the number it was issued for, so any
                        // edit invalidates it — fall back to a fresh OTP.
                        setPhoneIdToken(null);
                        clearFieldError();
                      }}
                      autoComplete="tel"
                      disabled={isLoading}
                      className="pl-12"
                    />
                  </div>
                  {phoneIdToken && (
                    <p className="text-xs text-[#8bc43e]">Verified — no new code needed</p>
                  )}
                </div>

                {/* Email Field (Optional) */}
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email <span className="text-muted-foreground font-normal">(Optional)</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); clearFieldError(); }}
                    autoComplete="email"
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    For charging receipts and account recovery
                  </p>
                </div>

                {/* Optional password — OTP sign-ups only. Their login method is SMS,
                    so this is an upgrade, not a requirement. */}
                {isOtpSignup && (
                  <div className="space-y-2">
                    {!showOptionalPassword ? (
                      <button
                        type="button"
                        onClick={() => setShowOptionalPassword(true)}
                        className="text-sm text-[#8bc43e] font-medium hover:underline"
                      >
                        + Set a password (optional)
                      </button>
                    ) : (
                      <>
                        <Label htmlFor="optionalPassword">
                          Password <span className="text-muted-foreground font-normal">(Optional)</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="optionalPassword"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Create a password"
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); clearFieldError(); }}
                            autoComplete="new-password"
                            disabled={isLoading}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            tabIndex={-1}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Confirm your password"
                          value={confirmPassword}
                          onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError(); }}
                          autoComplete="new-password"
                          disabled={isLoading}
                        />
                        <p className="text-xs text-muted-foreground">{PASSWORD_HELPER_TEXT}</p>
                      </>
                    )}
                    <p className="text-xs text-muted-foreground">
                      You can always sign in with an OTP sent to your number.
                    </p>
                  </div>
                )}

                {/* Error */}
                {displayError && (
                  <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                    {displayError}
                  </div>
                )}
              </div>

              {/* Social Auth Alternative */}
              <div className="mt-6">
                <div className="relative w-full my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                  </div>
                </div>
                <SocialAuthButtons />
              </div>
            </motion.div>
          )}

          {/* Step 2: Password */}
          {step === 2 && (
            <motion.div
              key="step2"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={slideTransition}
              className="w-full max-w-sm mx-auto"
            >

              <div className="space-y-4">
                {/* Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); clearFieldError(); }}
                      autoComplete="new-password"
                      disabled={isLoading}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">{PASSWORD_HELPER_TEXT}</p>
                </div>

                {/* Confirm Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError(); }}
                      autoComplete="new-password"
                      disabled={isLoading}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Error */}
                {displayError && (
                  <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                    {displayError}
                  </div>
                )}
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      {/* Sticky Bottom CTA */}
      <div
        className="relative z-10 px-6 pt-4 pb-6"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
      >
        {!isLastStep ? (
          <Button
            onClick={handleNext}
            className="w-full h-12 bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] hover:from-[#7ab32e] hover:to-[#5f8a26] text-white rounded-xl font-semibold shadow-lg shadow-[#8bc43e]/25 border-0"
            disabled={isLoading}
          >
            Continue
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            className="w-full h-12 bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] hover:from-[#7ab32e] hover:to-[#5f8a26] text-white rounded-xl font-semibold shadow-lg shadow-[#8bc43e]/25 border-0"
            disabled={isLoading || isSendingOtp}
          >
            {isLoading || isSendingOtp ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {isSendingOtp ? 'Sending verification...' : 'Creating account...'}
              </>
            ) : (
              'Create Account'
            )}
          </Button>
        )}

        {/* Login Link */}
        <p className="mt-4 text-sm text-muted-foreground text-center">
          Already have an account?{' '}
          <Link to="/login" className="text-[#8bc43e] font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </div>

      {/* Phone OTP Verification Modal */}
      <PhoneOtpModal
        open={showPhoneOtpModal}
        onOpenChange={setShowPhoneOtpModal}
        phone={phone.replace(/\D/g, '')}
        onVerified={handlePhoneVerified}
      />

      {/* Email OTP Verification Modal */}
      <OtpVerificationModal
        open={showOtpModal}
        onOpenChange={setShowOtpModal}
        email={email.trim()}
        purpose="registration"
        onVerified={handleOtpVerified}
        onResend={handleResendOtp}
        isVerifying={isVerifyingOtp}
        error={otpError}
      />
    </div>
  );
}

export default RegisterScreen;
